import { collection, query, where, getDocs, doc, deleteDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db, getCollectionPath } from './firebase';

/**
 * Cuando se cancela un turno (booking) ya sea por parte del jugador o del club, 
 * verificamos si existía un "partido abierto" (public_match) asociado a este turno. 
 * Si existe, lo eliminamos.
 */
export const cancelAssociatedMatch = async (bookingId: string) => {
  if (!bookingId) return;
  try {
    const q = query(collection(db, getCollectionPath('public_matches')), where('reservationId', '==', bookingId));
    const snapshot = await getDocs(q);
    
    // Un booking puede tener 1 public match asociado a él.
    const deletePromises = snapshot.docs.map(docSnap => deleteDoc(doc(db, getCollectionPath('public_matches'), docSnap.id)));
    
    if (deletePromises.length > 0) {
      await Promise.all(deletePromises);
      console.log(`Eliminados ${deletePromises.length} partidos públicos asociados a la reserva cancelada ${bookingId}`);
    }
  } catch (err) {
    console.error('Error al cancelar partido asociado a reserva:', err);
  }
};

import { Match, Tournament } from '../types';

export const getMatchTemporalState = (match: Match, tournament: Tournament | undefined) => {
  let isElimination = false;
  let bIdx = 0;

  if (match.id?.startsWith('bracket_')) {
    isElimination = true;
    const parts = match.id.split('_');
    if (parts.length >= 3) {
      bIdx = parseInt(parts[parts.length - 2], 10);
      if (bIdx === 999) {
         bIdx = tournament?.brackets ? tournament.brackets.length - 1 : 0;
      }
    }
  } else if (match.zoneIndex === undefined && match.round && !match.round.toLowerCase().includes('zona') && !match.round.toLowerCase().includes('grupo')) {
    isElimination = true;
  }

  const isMinutes = tournament?.scheduleTimeUnit === 'minutes';
  const isHours = tournament?.scheduleTimeUnit === 'hours';
  const timeUnitMs = isMinutes ? (60 * 1000) : isHours ? (60 * 60 * 1000) : (24 * 60 * 60 * 1000);

  let rawT = tournament?.startedAt || tournament?.createdAt || match.createdAt || 0;
  
  // Alinear el inicio a las 00:00:00 del día correspondiente
  const startDateObj = new Date(rawT);
  startDateObj.setHours(0, 0, 0, 0);
  let T = startDateObj.getTime();

  let daysPerRound = tournament?.scheduleDaysPerRound || 7;
  let roundNum = 1;

  if (isElimination && tournament?.eliminationStartDate) {
    T = tournament.eliminationStartDate; // Ya viene alineado a 00:00 desde AdminTournamentCard
    daysPerRound = tournament.eliminationDaysPerRound || daysPerRound;
    roundNum = bIdx + 1;
  } else {
    if (match.roundNumber !== undefined) {
      roundNum = match.roundNumber;
    } else {
      const roundMatch = match.round?.match(/\d+/);
      roundNum = roundMatch ? parseInt(roundMatch[0], 10) : 1;
    }
  }

  const now = Date.now();
  const roundStartTime = T + ((roundNum - 1) * daysPerRound * timeUnitMs);
  const roundEndTime = roundStartTime + (daysPerRound * timeUnitMs);


  let isActive = true;
  const isStarted = tournament?.status !== 'open' && tournament?.status !== 'cancelled';

  // Todas las fechas son visibles ("libre"), pero la carga de resultados se bloquea si matchState es 'inactivo'
  isActive = isStarted;

  let matchState: 'inactivo' | 'activo' | 'demorado' | 'en_proceso' | 'finalizado' | 'en_disputa' = 'inactivo';
  if (match.status === 'completed') {
    matchState = 'finalizado';
  } else if (match.status === 'impugned' || match.status === 'disputed') {
    matchState = 'en_disputa';
  } else if (match.status === 'pending_validation' || (match.status === 'pending' && match.reportedResult)) {
    matchState = 'en_proceso';
  } else if (match.status === 'pending') {
    let currentRoundNum = Math.floor((now - T) / (daysPerRound * timeUnitMs)) + 1;
    if (currentRoundNum < 1) currentRoundNum = 1;
    
    const advancedAllowed = tournament?.advancedDatesAllowed !== undefined ? tournament.advancedDatesAllowed : 'Todos';
    const isWithinAdvanceWindow = advancedAllowed === 'Todos' || roundNum <= (currentRoundNum + (advancedAllowed as number - 1));

    if (!isActive) {
      matchState = 'inactivo';
    } else if (now < roundStartTime && !isWithinAdvanceWindow) {
      matchState = 'inactivo';
    } else if (now > roundEndTime) {
      matchState = 'demorado';
    } else {
      matchState = 'activo';
    }
  }

  // Para la visualización de la tarjeta, restamos 1 milisegundo al endDate
  // Así el corte de las 00:00:00 del día 8 se muestra visualmente como las 23:59:59 del día 7
  const displayEndTime = timeUnitMs === 24 * 60 * 60 * 1000 ? roundEndTime - 1 : roundEndTime;

  return {
    isActive,
    startDate: new Date(roundStartTime),
    endDate: new Date(displayEndTime),
    roundNum,
    matchState
  };
};
